import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { 
  Stethoscope, Search, Layers, CheckCircle, 
  ArrowRight, Sparkles, Loader2, Target,
  Brain, ShieldCheck, Microscope, Database,
  ShieldAlert, Activity, BookOpen, Shield, X,
  GraduationCap, Clock
} from 'lucide-react';
import { ScenarioChallenge } from '../types';
import { StorageService } from '../services/storageService';
import { useAuth } from '../components/AuthBridge';
import { AdUnit } from '../components/AdUnit';
import { ModuleHeader } from '../components/ModuleHeader';
import { PageLoader } from '../components/PageLoader';
import { useThemeTokens } from '../hooks/useActiveTheme';

// Dynamic category resolver for Scenario challenges
const getScenarioCategory = (challenge: ScenarioChallenge) => {
  const title = challenge.title.toLowerCase();
  const desc = challenge.description.toLowerCase();
  
  if (
    title.includes('infection') || title.includes('precautions') || title.includes('isolation') || title.includes('sterile') || title.includes('wash') || title.includes('glove') || title.includes('ppe') ||
    desc.includes('infection') || desc.includes('hygiene') || desc.includes('germ')
  ) {
    return {
      name: 'Safety & Infection',
      icon: ShieldAlert,
      color: 'rose',
      badgeColor: 'bg-rose-50 border-rose-100 text-rose-700 dark:bg-rose-950/25 dark:border-rose-900/50 dark:text-rose-450',
      hoverColor: 'hover:border-rose-300 dark:hover:border-rose-800',
    };
  }
  
  if (
    title.includes('adl') || title.includes('bath') || title.includes('dress') || title.includes('feed') || title.includes('elimination') || title.includes('urinary') || title.includes('bowel') ||
    desc.includes('adl') || desc.includes('groom') || desc.includes('care') || desc.includes('morning') || desc.includes('night') || title.includes('personal') || title.includes('groom')
  ) {
    return {
      name: 'Personal & ADL Care',
      icon: BookOpen,
      color: 'amber',
      badgeColor: 'bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-950/25 dark:border-amber-900/50 dark:text-amber-450',
      hoverColor: 'hover:border-amber-300 dark:hover:border-amber-800',
    };
  }
  
  if (
    title.includes('vital') || title.includes('pulse') || title.includes('blood pressure') || title.includes('respiration') || title.includes('temperature') || title.includes('emergency') || title.includes('fire') || title.includes('chok') || title.includes('fall') ||
    desc.includes('bp') || desc.includes('emergency') || desc.includes('alert') || desc.includes('vitals')
  ) {
    return {
      name: 'Clinical & Emergencies',
      icon: Activity,
      color: 'emerald',
      badgeColor: 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-950/25 dark:border-emerald-900/50 dark:text-emerald-450',
      hoverColor: 'hover:border-emerald-300 dark:hover:border-emerald-800',
    };
  }

  return {
    name: 'Cognitive & Support',
    icon: Brain,
    color: 'indigo',
    badgeColor: 'bg-indigo-50 border-indigo-100 text-indigo-700 dark:bg-indigo-950/25 dark:border-indigo-900/50 dark:text-indigo-400',
    hoverColor: 'hover:border-indigo-300 dark:hover:border-indigo-800',
  };
};

export const ScenarioPicker: React.FC = () => {
  const { certId } = useParams<{ certId: string }>();
  const navigate = useNavigate();
  const { activeCert } = useAuth();
  const tokens = useThemeTokens();
  const [challenges, setChallenges] = useState<ScenarioChallenge[]>([]);
  const [activeCollection, setActiveCollection] = useState<string>('cna_scenarios');
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategoryTab, setActiveCategoryTab] = useState<string>('all');

  useEffect(() => {
    const load = async () => {
        const targetCert = certId || 'cna';
        setIsLoading(true);
        try {
            const res: any = await fetch(`/practice-exams/api/${targetCert}/scenarios?isActive=true`);
            const data = await res.json();
            setChallenges(data.items || []);
            if (data.collection) setActiveCollection(data.collection);
        } catch (e) {
            console.error("Failed to load scenario challenges:", e);
        } finally {
            setIsLoading(false);
        }
    };
    load();
  }, [certId]);

  const filtered = useMemo(() => {
    return challenges.filter(c => {
      const matchesSearch = c.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            c.description.toLowerCase().includes(searchTerm.toLowerCase());
      if (activeCategoryTab === 'all') return matchesSearch;
      const cat = getScenarioCategory(c).name;
      return matchesSearch && cat === activeCategoryTab;
    });
  }, [challenges, searchTerm, activeCategoryTab]);

  const uniqueCategories = useMemo(() => {
    const categoriesSet = new Set(challenges.map(c => getScenarioCategory(c).name));
    return Array.from(categoriesSet).sort();
  }, [challenges]);

  if (isLoading) {
      return <PageLoader message="Establishing Logic Sequencer..." />;
  }

  return (
    <div className="max-w-7xl mx-auto pb-32 animate-fade-in text-slate-900 dark:text-slate-100">
      
      <ModuleHeader 
        icon={Stethoscope}
        title="Scenario Lab"
        description="Answer questions based on real-life patient situations. These stories help you practice what to do in different caregiving scenes."
        badge="Patient Stories"
        color="indigo"
      />

      {/* THEORETICAL NARRATIVE FOCUS BOARD */}
      <div className={`p-6 md:p-8 text-white shadow-2xl relative overflow-hidden border rounded-[2.5rem] mb-8 ${
        tokens.isClinical
          ? 'bg-gradient-to-br from-[#0B2942] via-[#0D3C5C] to-[#071F33] border-teal-500/20'
          : tokens.isHighContrast
          ? 'bg-black text-amber-400 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
          : tokens.isMinimal
          ? 'bg-slate-900 border-slate-800'
          : tokens.isVibrant
          ? 'bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-950 border-violet-800'
          : 'bg-slate-900 border-white/5'
      }`}>
        <div className="absolute top-0 right-0 w-80 h-80 bg-indigo-500/10 blur-[80px] pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          
          <div className="lg:col-span-5 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-2.5 py-0.5 text-[8px] font-black rounded border uppercase tracking-widest flex items-center gap-1 leading-none pb-0.5 ${
                tokens.isClinical ? 'bg-teal-500/20 text-teal-300 border-teal-500/30' : tokens.isHighContrast ? 'bg-amber-300 text-black border-black' : tokens.isVibrant ? 'bg-violet-500/20 text-violet-300 border-violet-500/30' : 'bg-indigo-500/20 text-indigo-350 border-indigo-500/30'
              }`}>
                <Stethoscope size={10} className="animate-pulse" /> Scenario Lab
              </span>
              <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-350 text-[8px] font-black rounded border border-emerald-500/30 uppercase tracking-widest font-extrabold leading-none pb-0.5">
                Registry Sync Active
              </span>
            </div>
            <h1 className={`text-3xl md:text-4xl font-black tracking-tight text-white leading-none ${tokens.isClinical ? 'font-serif' : ''}`}>Scenario Lab</h1>
            <p className="text-xs text-slate-350 leading-relaxed font-semibold">
              Apply theory to real-world narrative case studies. Master the clinical bridge between board knowledge and patient care simulation exams.
            </p>
          </div>

          {/* Central Column Search Input */}
          <div className="lg:col-span-4 lg:px-4 py-1">
            <label className="text-[8px] font-black tracking-widest text-[#c7d2fe] uppercase mb-2 block font-extrabold">Scenario Case Search</label>
            <div className="relative group w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-400 transition-colors" size={16} />
              <input 
                type="text" 
                placeholder="Search case studies..." 
                value={searchTerm} 
                onChange={e => setSearchTerm(e.target.value)} 
                className="w-full pl-12 pr-10 py-3 bg-white/5 hover:bg-white/10 focus:bg-slate-950/40 border border-white/10 rounded-2xl outline-none text-white text-xs placeholder:text-slate-500 placeholder:font-bold font-bold focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all shadow-inner" 
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Central Column Progress Metric Card */}
          <div className="lg:col-span-3 bg-white/5 backdrop-blur-xl border border-white/10 p-5 rounded-[2rem] flex items-center justify-between shadow-xl">
             <div className="space-y-1 text-left">
                 <p className="text-[8px] font-black uppercase text-indigo-300 tracking-widest">Active Database</p>
                 <p className="text-xl font-black text-white truncate max-w-[150px]">{activeCollection.replace(/_/g, ' ')}</p>
                 <div className="flex items-center gap-2">
                   <span className="text-[9px] font-black text-[#a7f3d0]">{challenges.length} Scenarios Loaded</span>
                 </div>
             </div>
             <div className="relative w-12 h-12 flex items-center justify-center bg-indigo-500/10 rounded-2xl border border-indigo-500/20 shrink-0">
                <Database size={24} className="text-indigo-300" />
             </div>
          </div>

        </div>
      </div>

      {/* HORIZONTAL CATEGORY NAVIGATION */}
      <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 select-none no-scrollbar">
        <button 
          onClick={() => setActiveCategoryTab('all')}
          className={`px-5 py-2.5 rounded-full text-[10px] font-black tracking-widest transition-all border shrink-0 uppercase ${
            activeCategoryTab === 'all' 
              ? tokens.activeTab
              : tokens.inactiveTab
          }`}
        >
          All Cases ({challenges.length})
        </button>
        {uniqueCategories.map(cat => {
          const count = challenges.filter(c => getScenarioCategory(c).name === cat).length;
          return (
            <button 
              key={cat}
              onClick={() => setActiveCategoryTab(cat)}
              className={`px-5 py-2.5 rounded-full text-[10px] font-black tracking-widest transition-all border shrink-0 uppercase flex items-center gap-2 ${
                activeCategoryTab === cat 
                  ? tokens.activeTab
                  : tokens.inactiveTab
              }`}
            >
              <span>{cat}</span>
              <span className="opacity-70 text-[8px]">({count})</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-24 text-center border-4 border-dashed border-slate-100 dark:border-slate-800/80 flex flex-col items-center gap-8 grayscale opacity-60">
            <div className="w-24 h-24 bg-slate-50 dark:bg-slate-850 rounded-full flex items-center justify-center shadow-inner">
                <Brain size={48} className="text-slate-300 dark:text-slate-600" />
            </div>
            <div className="space-y-2">
              <p className="text-2xl font-black text-slate-900 dark:text-white tracking-tighter">Workspace Vacant</p>
              <p className="text-sm font-bold text-slate-500 dark:text-slate-405 tracking-widest">"No scenario cases match candidate selection filter."</p>
            </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
          {filtered.map((challenge) => {
            const catInfo = getScenarioCategory(challenge);
            const CatIcon = catInfo.icon;
            
            return (
              <button 
                key={challenge.id} 
                onClick={() => navigate(`/${certId}/scenario/${challenge.id}`)}
                className={`group relative bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 text-left transition-all hover:shadow-2xl overflow-hidden flex flex-col shadow-sm hover:translate-y-[-4px] duration-300 ${tokens.accentBorder}`}
              >
                {/* BEAUTIFIED HEADER */}
                <div className="aspect-[16/10] w-full relative overflow-hidden shrink-0">
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '16px 16px' }}></div>
                    
                    {/* Icon Identity */}
                    <div className="relative z-10 flex flex-col items-center gap-4 transition-transform duration-700 group-hover:scale-105">
                      <div className={`p-4 ${tokens.iconBg} backdrop-blur-xl rounded-[1.8rem] border border-white/10 shadow-2xl flex items-center justify-center`}>
                         <Stethoscope size={36} className="text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]" strokeWidth={1.5} />
                      </div>
                      <div className="text-center">
                        <p className={`text-[10px] font-black ${tokens.accentText} tracking-[0.3em] drop-shadow-md leading-none uppercase`}>CASE STUDY</p>
                        <div className="h-[2px] w-8 bg-white/40 mx-auto mt-3 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 px-3 py-1.5 bg-slate-900/40 backdrop-blur-md text-white rounded-full text-[9px] font-bold border border-white/10 flex items-center gap-1.5 z-20">
                    <Target size={11} className="text-white" /> {challenge.difficulty} RIGOR
                  </div>
                  <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/10 backdrop-blur-md text-white rounded-full text-[9px] font-black tracking-widest uppercase border border-white/5 flex items-center gap-1 z-20 leading-none">
                    <CatIcon size={10} /> {catInfo.name}
                  </div>
                  <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-white dark:from-slate-900 via-white/50 dark:via-slate-900/50 to-transparent z-10"></div>
                </div>
                
                {/* CARD DATA AREA */}
                <div className="p-8 md:p-10 space-y-4 flex-1 flex flex-col justify-between bg-white dark:bg-slate-900">
                  <div>
                    <div className="mb-3">
                      <span className={`text-[10px] font-black ${tokens.accentText} tracking-wider`}>
                        {challenge.questionCount} LOGIC SEQUENCES
                      </span>
                    </div>
                    <h3 className="text-lg font-black text-slate-955 dark:text-white leading-tight tracking-tight line-clamp-2">
                      {challenge.title}
                    </h3>
                    <p className="mt-3 text-xs text-slate-500 dark:text-slate-400 font-medium line-clamp-3">"{challenge.description}"</p>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800/80 mt-6">
                    <span className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2 uppercase">
                       <Clock size={12} /> Narrative Cases
                    </span>
                    <div className={`w-10 h-10 bg-slate-50 dark:bg-slate-800 group-hover:${tokens.primaryBtn} rounded-full flex items-center justify-center text-slate-400 dark:text-slate-500 group-hover:text-white dark:group-hover:text-white transition-all shadow-inner`}>
                       <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      
      <div className="mt-16">
        <AdUnit placement="dashboard" />
      </div>
    </div>
  );
};